import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

import { assertNonNullable } from "../../../utilities/assert";
import { isJsonRpc2ResponseArray, loadAndMapOpenRegisterRecord } from "../../../utilities/rpcHelper";
import { ModulfDataSelectors } from "../../../utilities/selectors/modulfDataSelectors";
import { RESOURCE_KEYS } from "../../../localization";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { executeWithoutValidation } from "../../../utilities/validationUtils";
import { executeBusy, updateWorkflowsActivityData } from "../../../utilities/workflowUtils";

import { CONFIRM_SELECT_OPENREGISTER_RECORD } from "../actions";
import { OpenregisterSearchModalDescriptor } from "../descriptors";

import Exception = JsonRpc2Response.Exception;

const documentService = new DocumentServiceFactory().getDocumentService();

export function* loadAndMapOpenRegisterRecordSaga(): SagaIterator<void> {
	yield* takeLatest(
		CONFIRM_SELECT_OPENREGISTER_RECORD,
		function* (action: Action<{ activityId: string; recordId: string }>) {
			yield* call(executeBusy, function* () {
				const { activityId, recordId } = action.payload;

				const activity = yield* select(ActivitySelectors.activityById(activityId));
				assertNonNullable(activity);

				if (!OpenregisterSearchModalDescriptor.isInstance(activity.descriptor)) {
					return;
				}

				const { openregisterPath, mappingConfigurationId, sourceActivityId } = activity.descriptor;

				const engineState = yield* select(FormEngineSelectors.engineState(sourceActivityId));
				assertNonNullable(engineState);

				const documentModel = ModelSelectors.documentModel()(engineState);
				const data = yield* select(ModulfDataSelectors.selectTaskDocument(sourceActivityId));

				if (!data) {
					return;
				}

				const path = openregisterPath.substring(openregisterPath.indexOf("/root/"));

				try {
					const updatedDocument = yield* call(
						executeWithoutValidation<Activity.Data.Document | Error>,
						sourceActivityId,
						function* (): SagaGenerator<Activity.Data.Document | Error> {
							return yield* call(
								loadAndMapOpenRegisterRecord,
								documentService.formatDates(data, documentModel),
								data.id,
								path,
								mappingConfigurationId,
								recordId
							);
						}
					);

					const parsedDocument = {
						...documentService.parseDates(updatedDocument, documentModel),
						modelId: documentModel.header.id
					};
					yield* call(updateWorkflowsActivityData, parsedDocument);
					yield* put(ActivityActions.cancel({ activityId }));
				} catch (error) {
					let message: Localizable;
					if (isJsonRpc2ResponseArray(error)) {
						const description = (error as JsonRpc2Response[])
							.filter(JsonRpc2Response.error.isInstance)
							.map(it => it.error.data)
							.filter(Exception.isInstance)
							.map(it => it.description)
							.pop();
						message = {
							key: RESOURCE_KEYS.features.notification.error.unexpectedError,
							args: {
								reason: {
									type: "plain",
									value: description?.default ?? "Kein Datensatz mit dieser ID gefunden."
								}
							}
						};
					} else {
						message = {
							key: RESOURCE_KEYS.features.notification.error.unexpectedError
						};
					}
					yield* call(putErrorNotification, message, error, activityId, "openregisterSelection");
				}
			});
		}
	);
}
